<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import LegalDrawer from '@/components/LegalDrawer.vue'
import { usePlatformContact } from '@/composables/usePlatformContact'
import { useContactMessage } from '@/composables/useContactMessage'
import { PLATFORM_CONTACT_TOPICS } from '@/types/contact'
import type { PlatformContactTopic } from '@/types/contact'

const MESSAGE_MIN = 20
const MESSAGE_MAX = 5000
const MAILTO_BODY_MAX = 1500
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const props = defineProps<{
  topic: PlatformContactTopic
  prefillName?: string
  prefillEmail?: string
}>()

const emit = defineEmits<{
  sent: [name: string]
}>()

type FieldKey = 'name' | 'email' | 'topic' | 'organisation_name' | 'message'

const { t } = useI18n()
const { infoEmail, privacyHtml } = usePlatformContact()
const { submitting, errors: serverErrors, generalError, submit, reset } = useContactMessage()

const fields = reactive({
  name: props.prefillName ?? '',
  email: props.prefillEmail ?? '',
  topic: props.topic as PlatformContactTopic,
  organisation_name: '',
  message: '',
})
const clientErrors = ref<Partial<Record<FieldKey, string>>>({})
const submitAttempted = ref(false)
const topicTouched = ref(false)
const sentName = ref<string | null>(null)
const successHeadingRef = ref<HTMLElement | null>(null)
const privacyOpen = ref(false)
const privacyTriggerRef = ref<HTMLElement | null>(null)

const mailtoFallback = computed(() => {
  const subject = encodeURIComponent(t(`contact.topics.${fields.topic}`))
  const body = encodeURIComponent(fields.message.slice(0, MAILTO_BODY_MAX))
  return `mailto:${infoEmail.value}?subject=${subject}&body=${body}`
})

watch(() => [fields.name, fields.email, fields.topic, fields.organisation_name, fields.message], (next, prev) => {
  const keys: FieldKey[] = ['name', 'email', 'topic', 'organisation_name', 'message']
  keys.forEach((key, index) => {
    if (next[index] !== prev[index]) clearServerError(key)
  })
})

watch(() => props.topic, (next) => {
  if (!topicTouched.value) {
    fields.topic = next
  }
})

watch(() => [props.prefillName, props.prefillEmail], ([name, email]) => {
  if (fields.name === '' && name) fields.name = name
  if (fields.email === '' && email) fields.email = email
})

function validate(): boolean {
  const errs: Partial<Record<FieldKey, string>> = {}
  const name = fields.name.trim()
  const email = fields.email.trim()
  const message = fields.message.trim()

  if (name === '') errs.name = t('contact.form.errors.name_required')
  if (email === '') errs.email = t('contact.form.errors.email_required')
  else if (!EMAIL_PATTERN.test(email)) errs.email = t('contact.form.errors.email_invalid')
  if (!PLATFORM_CONTACT_TOPICS.includes(fields.topic)) errs.topic = t('contact.form.errors.topic_required')
  if (message === '') errs.message = t('contact.form.errors.message_required')
  else if (message.length < MESSAGE_MIN) errs.message = t('contact.form.errors.message_min', { min: MESSAGE_MIN })
  else if (message.length > MESSAGE_MAX) errs.message = t('contact.form.errors.message_max', { max: MESSAGE_MAX })

  clientErrors.value = errs
  return Object.keys(errs).length === 0
}

// After the first submit attempt, re-validate when a field loses focus.
function handleBlur(): void {
  if (submitAttempted.value) validate()
}

// Editing a field invalidates the server error that was reported for its previous value.
function clearServerError(field: FieldKey): void {
  if (serverErrors.value[field] === undefined) return
  const { [field]: _removed, ...rest } = serverErrors.value
  serverErrors.value = rest
}

function fieldError(field: FieldKey): string | null {
  return serverErrors.value[field]?.[0] ?? clientErrors.value[field] ?? null
}

function describedBy(field: FieldKey): string | undefined {
  return fieldError(field) ? `contact-${field}-error` : undefined
}

async function handleSubmit(): Promise<void> {
  submitAttempted.value = true
  reset()
  if (!validate()) return

  const organisation = fields.organisation_name.trim()
  const ok = await submit({
    name: fields.name.trim(),
    email: fields.email.trim(),
    topic: fields.topic,
    message: fields.message.trim(),
    organisation_name: organisation === '' ? null : organisation,
  })

  if (ok) {
    sentName.value = fields.name.trim()
    emit('sent', sentName.value)
    await nextTick()
    successHeadingRef.value?.focus()
  }
}

function sendAnother(): void {
  sentName.value = null
  fields.message = ''
  fields.organisation_name = ''
  fields.topic = props.topic
  topicTouched.value = false
  clientErrors.value = {}
  submitAttempted.value = false
  reset()
}

const inputClass = 'mt-1 block w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500'
</script>

<template>
  <div>
    <!-- Success state -->
    <div
      v-if="sentName !== null"
      role="status"
      class="rounded-2xl bg-emerald-50 border border-emerald-200 p-8 text-center"
    >
      <div class="mx-auto mb-4 w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3
        ref="successHeadingRef"
        tabindex="-1"
        class="text-xl font-bold text-slate-900 focus:outline-none"
      >
        {{ t('contact.form.success_title', { name: sentName }) }}
      </h3>
      <p class="mt-2 text-slate-600">{{ t('contact.form.success_body') }}</p>
      <button
        type="button"
        class="mt-6 inline-flex items-center justify-center border-2 border-slate-300 text-slate-700 hover:border-slate-400 font-semibold rounded-lg px-6 py-2.5 text-sm transition-colors"
        @click="sendAnother"
      >
        {{ t('contact.form.send_another') }}
      </button>
    </div>

    <!-- Form -->
    <form v-else novalidate class="space-y-5" @submit.prevent="handleSubmit">
      <div
        v-if="generalError"
        role="alert"
        class="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
      >
        <i18n-t v-if="generalError === 'contact.form.errors.throttled'" keypath="contact.form.errors.throttled" tag="p">
          <template #email>
            <a :href="`mailto:${infoEmail}`" class="font-semibold underline">{{ infoEmail }}</a>
          </template>
        </i18n-t>
        <template v-else>
          <p>{{ t('contact.form.errors.generic') }}</p>
          <a :href="mailtoFallback" class="mt-2 inline-block font-semibold underline">
            {{ t('contact.form.errors.mailto_fallback') }}
          </a>
        </template>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label for="contact-name" class="block text-sm font-medium text-slate-700">{{ t('contact.form.name') }}</label>
          <input
            id="contact-name"
            v-model="fields.name"
            type="text"
            name="name"
            autocomplete="name"
            maxlength="255"
            :class="[inputClass, fieldError('name') ? 'border-red-500' : 'border-slate-300']"
            :aria-invalid="fieldError('name') ? 'true' : undefined"
            :aria-describedby="describedBy('name')"
            @blur="handleBlur"
          />
          <p v-if="fieldError('name')" id="contact-name-error" class="mt-1 text-sm text-red-600">{{ fieldError('name') }}</p>
        </div>

        <div>
          <label for="contact-email" class="block text-sm font-medium text-slate-700">{{ t('contact.form.email') }}</label>
          <input
            id="contact-email"
            v-model="fields.email"
            type="email"
            name="email"
            autocomplete="email"
            maxlength="255"
            :class="[inputClass, fieldError('email') ? 'border-red-500' : 'border-slate-300']"
            :aria-invalid="fieldError('email') ? 'true' : undefined"
            :aria-describedby="describedBy('email')"
            @blur="handleBlur"
          />
          <p v-if="fieldError('email')" id="contact-email-error" class="mt-1 text-sm text-red-600">{{ fieldError('email') }}</p>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label for="contact-topic" class="block text-sm font-medium text-slate-700">{{ t('contact.form.topic') }}</label>
          <select
            id="contact-topic"
            v-model="fields.topic"
            name="topic"
            :class="[inputClass, fieldError('topic') ? 'border-red-500' : 'border-slate-300']"
            :aria-invalid="fieldError('topic') ? 'true' : undefined"
            :aria-describedby="describedBy('topic')"
            @change="topicTouched = true"
          >
            <option v-for="topic in PLATFORM_CONTACT_TOPICS" :key="topic" :value="topic">
              {{ t(`contact.topics.${topic}`) }}
            </option>
          </select>
          <p v-if="fieldError('topic')" id="contact-topic-error" class="mt-1 text-sm text-red-600">{{ fieldError('topic') }}</p>
        </div>

        <div>
          <label for="contact-organisation" class="block text-sm font-medium text-slate-700">{{ t('contact.form.organisation') }}</label>
          <input
            id="contact-organisation"
            v-model="fields.organisation_name"
            type="text"
            name="organisation_name"
            autocomplete="organization"
            maxlength="255"
            :class="[inputClass, fieldError('organisation_name') ? 'border-red-500' : 'border-slate-300']"
            :aria-invalid="fieldError('organisation_name') ? 'true' : undefined"
            :aria-describedby="describedBy('organisation_name')"
          />
          <p v-if="fieldError('organisation_name')" id="contact-organisation_name-error" class="mt-1 text-sm text-red-600">
            {{ fieldError('organisation_name') }}
          </p>
        </div>
      </div>

      <div>
        <label for="contact-message" class="block text-sm font-medium text-slate-700">{{ t('contact.form.message') }}</label>
        <textarea
          id="contact-message"
          v-model="fields.message"
          name="message"
          rows="6"
          :maxlength="MESSAGE_MAX"
          :class="[inputClass, fieldError('message') ? 'border-red-500' : 'border-slate-300']"
          :aria-invalid="fieldError('message') ? 'true' : undefined"
          :aria-describedby="describedBy('message')"
          @blur="handleBlur"
        ></textarea>
        <div class="mt-1 flex items-start justify-between gap-4">
          <p v-if="fieldError('message')" id="contact-message-error" class="text-sm text-red-600">{{ fieldError('message') }}</p>
          <span v-else></span>
          <p class="text-xs text-slate-400 shrink-0">
            {{ t('contact.form.message_counter', { count: fields.message.length, max: MESSAGE_MAX }) }}
          </p>
        </div>
      </div>

      <i18n-t keypath="contact.form.privacy_line" tag="p" class="text-xs text-slate-500">
        <template #privacy>
          <button
            ref="privacyTriggerRef"
            type="button"
            class="font-semibold text-emerald-600 hover:text-emerald-500 underline"
            @click="privacyOpen = true"
          >
            {{ t('contact.form.privacy_link') }}
          </button>
        </template>
      </i18n-t>

      <button
        type="submit"
        :disabled="submitting"
        class="inline-flex items-center justify-center bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-8 py-3 text-base transition-colors"
      >
        {{ submitting ? t('common.loading') : t('contact.form.submit') }}
      </button>
    </form>

    <LegalDrawer
      :open="privacyOpen"
      initial-document="privacy_policy"
      :race-rules="null"
      :terms-and-conditions="null"
      :startline-terms="null"
      :privacy-policy="privacyHtml"
      :return-focus-to="privacyTriggerRef"
      @update:open="privacyOpen = $event"
    />
  </div>
</template>
